import React from 'react'
import {
  stepAgentConfig,
  stepInteractContrastUI,
  stepSelectWindowsNormalize,
  stepAgentConfigPipeline,
  stepConfigureAppSettingsAzureNormalize
} from './common'
import {
  stepContainerAddContrastConfigurationToImage,
  stepContainerRunYourContainer,
  stepContainerDotnetRunInstaller
} from './common-container'
import { formatStyles } from '../../../../react-intl-helpers'
import { logo_azure, logo_docker, logo_dotnet, logo_pipeline } from '../images'
import { ImageIcon } from '../common/image-icon'
import { StepContent } from '../agent-wizard-steps'
import { OPERATING_SYSTEM } from '../common/constants'
import {
  IconBitbucket,
  IconGithub
} from '@contrast-security-inc/design-system-foundations'

const commonFinalSteps = [stepInteractContrastUI]

const selectMethodNormalizeAgent = {
  titleMsg: 'title_selectAMethodInstallAgent',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'installer',
        Content: {
          message: 'title_installWithContrastInstaller'
        },
        icon: <ImageIcon src={logo_dotnet} alt="installer" />
      },
      {
        value: 'container',
        Content: {
          message: 'title_installContainer'
        },
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'azure',
        Content: {
          message: 'title_installWithAzure'
        },
        icon: <ImageIcon src={logo_azure} alt="azure" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const stepsWindowsInstallerNormalizeAgentDownload = [
  {
    titleMsg: 'title_downloadInstaller',
    defaultIsOpen: true,
    Content: {
      message: 'detail_dotnetDownloadInstaller',
      values: {
        code: `Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.FrameworkIIS.Installer/ -OutFile Contrast.FrameworkIIS.Installer.zip
Expand-Archive .\\Contrast.FrameworkIIS.Installer.zip -DestinationPath .\\Contrast.FrameworkIIS.Installer
`
      }
    }
  },
  stepAgentConfig,
  stepContainerDotnetRunInstaller,
  ...commonFinalSteps
]

const stepSelectDockerMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethod',
  Content: {
    options: [
      {
        value: 'docker',
        Content: 'Dockerfile',
        icon: <ImageIcon src={logo_docker} alt="Dockerfile" />
      }
    ]
  }
}

const buildImageWithAgentStep = {
  titleMsg: 'title_buildImageWithAgent',
  defaultIsOpen: true,
  branchKey: 'dotnet-core-method-docker',
  Content: () => {
    return (
      <>
        <StepContent
          step={{
            Content: {
              message: 'detail_addAgentToImage',
              values: {
                code: `COPY --from=contrast/agent-dotnet-framework:latest C:\\Contrast C:\\Contrast
ENV COR_ENABLE_PROFILING=1 \\
COR_PROFILER={EFEB8EE0-6D39-4347-A5FE-4D0C88BC5BC1} \\
COR_PROFILER_PATH_32=C:\\Contrast\\runtimes\\win-x86\\native\\ContrastProfiler.dll \\
COR_PROFILER_PATH_64=C:\\Contrast\\runtimes\\win-x64\\native\\ContrastProfiler.dll`
              }
            }
          }}
        />
        <StepContent
          step={{
            Content: {
              message: 'detail_addBuildImage',
              values: {
                code: 'docker build -t my-app-with-contrast .'
              }
            }
          }}
        />
      </>
    )
  }
}

const stepsWindowsContainerNormalizeAgentDownload = [
  {
    ...stepSelectDockerMethod,
    branchKey: 'dotnet-container-method'
  },
  buildImageWithAgentStep,
  stepAgentConfig,
  stepContainerAddContrastConfigurationToImage,
  stepContainerRunYourContainer,
  ...commonFinalSteps
]

const stepsWindowsAzureNormalizeAgentDownload = [
  {
    titleMsg: 'title_selectAnApplication',
    defaultIsOpen: true,
    Content: {
      message: 'detail_dotnetAzureSelectAnApplication',
      values: formatStyles.link({
        isExternal: true,
        href: 'https://learn.microsoft.com/en-us/azure/app-service/quickstart-dotnetcore?tabs=netframework48&pivots=development-environment-vs'
      })
    }
  },
  stepConfigureAppSettingsAzureNormalize,
  {
    titleMsg: 'title_addExtensionForApplication',
    Content: {
      message: 'detail_dotnetAzureAddExtensionForApplication',
      values: formatStyles.link({
        isExternal: true,
        href: 'https://portal.azure.com/#home'
      })
    }
  },
  ...commonFinalSteps
]

const stepSelectPipelineMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethodPipeline',
  Content: {
    options: [
      {
        value: 'github',
        Content: 'Github',
        icon: <IconGithub color="black" />
      },
      {
        value: 'bitbucket',
        Content: 'Bitbucket',
        icon: <IconBitbucket color="blue50" />
      }
    ]
  }
}

const githubInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubDotnet',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run: |
    - Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ -OutFile Contrast.SensorsNetCore.zip
    - Expand-Archive .\\Contrast.SensorsNetCore.zip -DestinationPath .\\Contrast.SensorsNetCore
    - Move-Item .\\Contrast.SensorsNetCore\\contentFiles\\any\\netstandard2.0\\contrast .\\
`,
      code2: `env:
  CORECLR_PROFILER_PATH_64: <DirectoryRoot>\\contrast\\runtimes\\win-x64\\native\\ContrastProfiler.dll
  CORECLR_PROFILER_PATH_32: <DirectoryRoot>\\contrast\\runtimes\\win-x86\\native\\ContrastProfiler.dll
  CORECLR_PROFILER: {8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
  CORECLR_ENABLE_PROFILING: 1
  CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }}
  CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }}
  CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }}
`
    }
  }
}

const bitbucketInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketDotnet',
    values: {
      code1: `- step:
  script:
    - Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ -OutFile Contrast.SensorsNetCore.zip
    - Expand-Archive .\\Contrast.SensorsNetCore.zip -DestinationPath .\\Contrast.SensorsNetCore
    - Move-Item .\\Contrast.SensorsNetCore\\contentFiles\\any\\netstandard2.0\\contrast .\\
`,
      code2: `- set CORECLR_PROFILER_PATH_64=<DirectoryRoot>\\contrast\\runtimes\\win-x64\\native\\ContrastProfiler.dll
- set CORECLR_PROFILER_PATH_32=<DirectoryRoot>\\contrast\\runtimes\\win-x86\\native\\ContrastProfiler.dll
- set CORECLR_ENABLE_PROFILING=1
- set CORECLR_PROFILER={8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
`
    }
  }
}

const pipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'dotnet-pipeline-method',
    branches: {
      github: [
        githubInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ],
      bitbucket: [
        bitbucketInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ]
    }
  }
]

export const dotnetWizardStepsNormalizeAgentDownload = [
  {
    ...stepSelectWindowsNormalize,
    branchKey: OPERATING_SYSTEM.KEY
  },
  {
    ...selectMethodNormalizeAgent,
    branchKey: 'dotnet-method-windows',
    branches: {
      installer: stepsWindowsInstallerNormalizeAgentDownload,
      container: stepsWindowsContainerNormalizeAgentDownload,
      azure: stepsWindowsAzureNormalizeAgentDownload,
      pipeline: pipelineSteps
    }
  }
]
